import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createChart, HistogramSeries } from 'lightweight-charts';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea
} from 'recharts';
import API_BASE_URL from '../config/api';

const REFRESH_INTERVAL_MS       = 5000;
const AMW_REFRESH_INTERVAL_MS   = 10000;
const PRICE_REFRESH_INTERVAL_MS = 30000;

const BUY_COLOR  = '#26a69a';
const SELL_COLOR = '#ef5350';

const LINE_COLORS = ['#58a6ff', '#3fb950', '#ff7b72', '#d2a8ff', '#ffa657', '#79c0ff', '#f0883e'];

const CHART_OPTIONS = {
  layout: { background: { color: '#0d1117' }, textColor: '#c9d1d9' },
  grid:   { vertLines: { color: '#21262d' }, horzLines: { color: '#21262d' } },
  crosshair: { vertLine: { color: '#444c56' }, horzLine: { color: '#444c56' } },
  timeScale: { borderColor: '#30363d', timeVisible: true, secondsVisible: true },
  rightPriceScale: { borderColor: '#30363d' },
  autoSize: true,
};
const BAR_OPTS = { priceLineVisible: false, lastValueVisible: true };

const WINDOW_OPTIONS = [
  { label: '1 sec',  value: '1s',  rangeLabel: 'last 1 hour'   },
  { label: '5 sec',  value: '5s',  rangeLabel: 'last 5 hours'  },
  { label: '10 sec', value: '10s', rangeLabel: 'last 10 hours' },
  { label: '1 min',  value: '1m',  rangeLabel: 'last 24 hours' },
  { label: '10 min', value: '10m', rangeLabel: 'all available' },
];

const AMW_TICK_INTERVAL_MS = {
  '1s': 5*60*1000, '5s': 10*60*1000, '10s': 10*60*1000,
  '1m': 10*60*1000, '10m': 30*60*1000,
};

const PRICE_BUCKET_OPTIONS = [
  { label: '1 sec',  value: '1s'  },
  { label: '5 sec',  value: '5s'  },
  { label: '10 sec', value: '10s' },
  { label: '1 min',  value: '1m'  },
  { label: '10 min', value: '10m' },
];

const PRICE_TICK_INTERVAL_MS = {
  '1s': 10*60*1000, '5s': 10*60*1000, '10s': 10*60*1000,
  '1m': 10*60*1000, '10m': 60*60*1000,
};

const PRICE_LABEL_EVERY_MIN = {
  '1s': 60, '5s': 60, '10s': 60, '1m': 30, '10m': 60,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

// Pick a "nice" tick interval for any visible time range (targets ~8-12 ticks).
const NICE_INTERVALS = [
  1000, 5000, 10000, 30000,
  60000, 5*60000, 10*60000, 15*60000, 30*60000,
  3600000, 2*3600000, 4*3600000, 6*3600000, 12*3600000, 24*3600000,
];
function niceInterval(rangeMs) {
  const target = rangeMs / 10;
  return NICE_INTERVALS.reduce((best, iv) =>
    Math.abs(iv - target) < Math.abs(best - target) ? iv : best
  );
}

function buildTicksFromDomain(minMs, maxMs, intervalMs) {
  if (!minMs || !maxMs || minMs >= maxMs) return [];
  const ticks = [];
  const start = Math.ceil(minMs / intervalMs) * intervalMs;
  for (let t = start; t <= maxMs; t += intervalMs) ticks.push(t);
  return ticks;
}

function formatTime(timeMs, win) {
  const d  = new Date(timeMs);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  if (win === '1s' || win === '5s') return `${hh}:${mm}:${ss}`;
  if (win === '10s' || win === '1m') return `${hh}:${mm}`;
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mo}-${dd} ${hh}:${mm}`;
}

function formatHHMM(ms) {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const tooltipStyle = {
  background: '#1c2128', border: '1px solid #30363d',
  padding: '6px 10px', fontSize: '11px',
  fontFamily: "'Courier New', Courier, monospace",
};
const selectStyle = {
  background: '#0d1117', border: '1px solid #30363d',
  borderRadius: '4px', color: '#c9d1d9',
  fontSize: '12px', padding: '2px 6px', cursor: 'pointer',
  fontFamily: "'Courier New', Courier, monospace",
};
const TAB_BASE   = { padding: '6px 18px', fontSize: '12px', cursor: 'pointer', background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#8b949e', fontFamily: "'Courier New', Courier, monospace" };
const TAB_ACTIVE = { ...TAB_BASE, color: '#c9d1d9', borderBottom: '2px solid #58a6ff' };

// ── Tooltips ──────────────────────────────────────────────────────────────────

const AmwTooltip = ({ active, payload, label, win }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={tooltipStyle}>
      <p style={{ color: '#8b949e', margin: '0 0 2px 0' }}>
        {typeof label === 'number' ? formatTime(label, win) : label}
      </p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color, margin: 0 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(6) : '---'}
        </p>
      ))}
    </div>
  );
};

const PriceTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const fmt = v => v.toLocaleString(undefined, { maximumFractionDigits: 8 });

  // Separate main exchange lines from bid/ask lines (bid/ask Lines have __ in dataKey)
  const mainEntries = payload.filter(p =>
    p.value != null &&
    !p.dataKey.endsWith('__bid_price') &&
    !p.dataKey.endsWith('__ask_price')
  );

  // Build per-exchange bid/ask map from the payload (populated by the bid/ask Lines)
  const obMap = {};
  for (const p of payload) {
    if (p.value == null) continue;
    if (p.dataKey.endsWith('__bid_price')) {
      const exch = p.dataKey.slice(0, -'__bid_price'.length);
      if (!obMap[exch]) obMap[exch] = {};
      obMap[exch].bidPrice = p.value;
    } else if (p.dataKey.endsWith('__ask_price')) {
      const exch = p.dataKey.slice(0, -'__ask_price'.length);
      if (!obMap[exch]) obMap[exch] = {};
      obMap[exch].askPrice = p.value;
    }
  }

  let maxEntry = null, minEntry = null;
  for (const p of mainEntries) {
    if (!maxEntry || p.value > maxEntry.value) maxEntry = p;
    if (!minEntry || p.value < minEntry.value) minEntry = p;
  }

  const diff    = maxEntry && minEntry ? maxEntry.value - minEntry.value : null;
  const diffPct = diff !== null && minEntry.value !== 0
    ? (diff / minEntry.value * 100).toFixed(4)
    : null;

  return (
    <div style={tooltipStyle}>
      <p style={{ color: '#8b949e', margin: '0 0 2px 0' }}>
        {typeof label === 'number' ? formatHHMM(label) : label}
      </p>
      {mainEntries.map(p => {
        const exch   = p.dataKey;
        const ob     = obMap[exch];
        const dp     = p.payload;
        const bidQty = dp?.[`${exch}__bid_qty`];
        const askQty = dp?.[`${exch}__ask_qty`];
        const hasOB  = ob || bidQty != null || askQty != null;
        return (
          <div key={exch} style={{ marginBottom: hasOB ? '4px' : 0 }}>
            <p style={{ color: p.stroke || p.color, margin: 0 }}>
              {p.name}: {fmt(p.value)}
            </p>
            {hasOB && (
              <div style={{ paddingLeft: '8px', fontSize: '10px', color: '#8b949e' }}>
                <p style={{ margin: 0 }}>
                  bid:{ob?.bidPrice != null ? ` ${fmt(ob.bidPrice)}` : ''}
                  {bidQty != null ? `  qty: ${fmt(bidQty)}` : ''}
                </p>
                <p style={{ margin: 0 }}>
                  ask:{ob?.askPrice != null ? ` ${fmt(ob.askPrice)}` : ''}
                  {askQty != null ? `  qty: ${fmt(askQty)}` : ''}
                </p>
              </div>
            )}
          </div>
        );
      })}
      {maxEntry && (
        <div style={{ borderTop: '1px solid #30363d', marginTop: '4px', paddingTop: '3px' }}>
          <p style={{ color: '#8b949e', margin: 0 }}>
            Max: {fmt(maxEntry.value)} [{maxEntry.name}]
          </p>
          <p style={{ color: '#8b949e', margin: 0 }}>
            Min: {fmt(minEntry.value)} [{minEntry.name}]
          </p>
          <p style={{ color: '#8b949e', margin: 0 }}>
            Diff: {fmt(diff)}{diffPct !== null ? ` [${diffPct}%]` : ''}
          </p>
        </div>
      )}
    </div>
  );
};

// ── Line style indicator (mini SVG) ──────────────────────────────────────────

const LineIndicator = ({ dashArray, color, opacity = 1 }) => (
  <svg width="28" height="12" style={{ flexShrink: 0, display: 'block' }}>
    <line x1="1" y1="6" x2="27" y2="6"
      stroke={color} strokeWidth="1.5"
      strokeDasharray={dashArray || undefined}
      opacity={opacity} />
  </svg>
);

// ── Zoom hint overlay ─────────────────────────────────────────────────────────

const ZoomHint = ({ zoomed, onReset }) => (
  <div style={{
    position: 'absolute', bottom: 32, right: 24,
    fontSize: '10px', color: '#444c56', pointerEvents: zoomed ? 'auto' : 'none',
    display: 'flex', alignItems: 'center', gap: '6px',
  }}>
    {!zoomed && <span>pinch or ctrl+scroll to zoom</span>}
    {zoomed && (
      <button onClick={onReset} style={{
        background: '#21262d', border: '1px solid #30363d',
        borderRadius: '4px', color: '#8b949e',
        fontSize: '10px', padding: '2px 8px', cursor: 'pointer',
        fontFamily: "'Courier New', Courier, monospace",
      }}>
        reset zoom
      </button>
    )}
  </div>
);

// ── useChartZoom hook — attaches wheel zoom to a div ref ──────────────────────

function useChartZoom(dataRef) {
  const containerRef = useRef(null);
  const [zoom, setZoom]   = useState(null); // null = full range, or [minMs, maxMs]

  const resetZoom = useCallback(() => setZoom(null), []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (!e.ctrlKey) return;          // pinch gesture (or ctrl+scroll)
      e.preventDefault();

      const data = dataRef.current;
      if (!data || data.length < 2) return;

      const dataMin = data[0].time_ms;
      const dataMax = data[data.length - 1].time_ms;

      setZoom(prev => {
        const [curMin, curMax] = prev ?? [dataMin, dataMax];
        const range    = curMax - curMin;
        // Math.pow gives smooth, proportional zoom regardless of delta magnitude
        const factor   = Math.pow(1.001, e.deltaY);
        const newRange = Math.min(range * factor, dataMax - dataMin);

        // Keep the cursor's time position fixed while zooming
        const rect        = el.getBoundingClientRect();
        const cursorRatio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const cursorTime  = curMin + cursorRatio * range;

        const newMin = Math.max(dataMin, cursorTime - cursorRatio * newRange);
        const newMax = Math.min(dataMax, newMin + newRange);

        // Snap back to full range when zoomed fully out
        if (newMin <= dataMin && newMax >= dataMax) return null;
        return [newMin, newMax];
      });
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [dataRef]);

  return { containerRef, zoom, resetZoom };
}

// ── Main component ────────────────────────────────────────────────────────────

const ChartPage = () => {
  const [searchParams] = useSearchParams();
  const exchange     = searchParams.get('exchange')     || '';
  const symbol       = searchParams.get('symbol')       || '';
  const contractType = searchParams.get('contractType') || 'perpetual';

  const [activeTab, setActiveTab] = useState('trades');

  // ── Histogram ────────────────────────────────────────────────────────────────
  const containerRef  = useRef(null);
  const chartRef      = useRef(null);
  const buySeriesRef  = useRef(null);
  const sellSeriesRef = useRef(null);

  const [error,      setError]      = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [tradeCount, setTradeCount] = useState(0);

  // ── AMW ───────────────────────────────────────────────────────────────────────
  const [amwWindow, setAmwWindow] = useState('10s');
  const [amwData,   setAmwData]   = useState([]);
  const [amwError,  setAmwError]  = useState(null);
  const amwDataRef = useRef(amwData);
  useEffect(() => { amwDataRef.current = amwData; }, [amwData]);

  const { containerRef: amwChartRef, zoom: amwZoom, resetZoom: resetAmwZoom } = useChartZoom(amwDataRef);
  // Reset zoom when the window changes
  useEffect(() => { resetAmwZoom(); }, [amwWindow, resetAmwZoom]);

  // ── Price ─────────────────────────────────────────────────────────────────────
  const [priceBucket, setPriceBucket] = useState('1m');
  const [priceDataMap,  setPriceDataMap]  = useState({});
  const [priceErrorMap, setPriceErrorMap] = useState({});
  const [activeExchanges,  setActiveExchanges]  = useState(() => [exchange]);
  const [hiddenDatasets,   setHiddenDatasets]   = useState(new Set());
  const [colorMap,         setColorMap]         = useState(() => ({ [exchange]: LINE_COLORS[0] }));
  const colorIdxRef = useRef(1);

  const [availableExchanges, setAvailableExchanges] = useState([]);

  // Stable refs for interval callbacks
  const priceBucketRef     = useRef(priceBucket);
  const activeExchangesRef = useRef(activeExchanges);
  useEffect(() => { priceBucketRef.current     = priceBucket;     }, [priceBucket]);
  useEffect(() => { activeExchangesRef.current = activeExchanges; }, [activeExchanges]);

  // ── Fetch functions ───────────────────────────────────────────────────────────

  const fetchHistogram = useCallback(async () => {
    if (!buySeriesRef.current) return;
    try {
      const url = `${API_BASE_URL}/api/orderbook-chart` +
        `?exchange=${encodeURIComponent(exchange)}&symbol=${encodeURIComponent(symbol)}` +
        `&contractType=${encodeURIComponent(contractType)}`;
      const json = await fetch(url).then(r => r.json());
      if (!json.success) { setError(json.error || 'Failed'); return; }
      if (json.buy_quantity?.length)  buySeriesRef.current.setData(json.buy_quantity);
      if (json.sell_quantity?.length) sellSeriesRef.current.setData(
        json.sell_quantity.map(d => ({ ...d, value: -d.value }))
      );
      chartRef.current?.timeScale().fitContent();
      setTradeCount(json.trade_count || 0);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) { setError(err.message); }
  }, [exchange, symbol, contractType]);

  const fetchAmw = useCallback(async (win) => {
    try {
      const url = `${API_BASE_URL}/api/orderbook-average-moving-window` +
        `?exchange=${encodeURIComponent(exchange)}&symbol=${encodeURIComponent(symbol)}` +
        `&contractType=${encodeURIComponent(contractType)}&window=${encodeURIComponent(win)}`;
      const json = await fetch(url).then(r => r.json());
      if (!json.success) { setAmwError(json.error || 'Failed'); return; }
      setAmwData((json.data || []).map(d => ({ time_ms: d.time_ms, qty_per_sec: d.qty_per_sec })));
      setAmwError(null);
    } catch (err) { setAmwError(err.message); }
  }, [exchange, symbol, contractType]);

  const fetchPriceLine = useCallback(async (exch, bucket) => {
    try {
      const url = `${API_BASE_URL}/api/orderbook-price-history` +
        `?exchange=${encodeURIComponent(exch)}&symbol=${encodeURIComponent(symbol)}` +
        `&contractType=${encodeURIComponent(contractType)}&bucket=${encodeURIComponent(bucket)}`;
      const json = await fetch(url).then(r => r.json());
      if (!json.success) { setPriceErrorMap(prev => ({ ...prev, [exch]: json.error || 'Failed' })); return; }
      setPriceDataMap(prev  => ({ ...prev,  [exch]: json.data || [] }));
      setPriceErrorMap(prev => { const n = { ...prev }; delete n[exch]; return n; });
    } catch (err) { setPriceErrorMap(prev => ({ ...prev, [exch]: err.message })); }
  }, [symbol, contractType]);

  const fetchAvailableExchanges = useCallback(async () => {
    try {
      const url = `${API_BASE_URL}/api/orderbook-streams` +
        `?symbol=${encodeURIComponent(symbol)}&contractType=${encodeURIComponent(contractType)}`;
      const json = await fetch(url).then(r => r.json());
      if (json.success) setAvailableExchanges(json.exchanges || []);
    } catch {}
  }, [symbol, contractType]);

  // ── Effects ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, CHART_OPTIONS);
    chartRef.current = chart;
    buySeriesRef.current  = chart.addSeries(HistogramSeries, { ...BAR_OPTS, color: BUY_COLOR,  title: 'buy qty'  });
    sellSeriesRef.current = chart.addSeries(HistogramSeries, { ...BAR_OPTS, color: SELL_COLOR, title: 'sell qty' });
    fetchHistogram();
    const iv = setInterval(fetchHistogram, REFRESH_INTERVAL_MS);
    return () => { clearInterval(iv); chart.remove(); chartRef.current = buySeriesRef.current = sellSeriesRef.current = null; };
  }, [fetchHistogram]);

  useEffect(() => {
    setAmwData([]);
    fetchAmw(amwWindow);
    const iv = setInterval(() => fetchAmw(amwWindow), AMW_REFRESH_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [fetchAmw, amwWindow]);

  useEffect(() => {
    activeExchangesRef.current.forEach(exch => fetchPriceLine(exch, priceBucketRef.current));
    const iv = setInterval(() => {
      activeExchangesRef.current.forEach(exch => fetchPriceLine(exch, priceBucketRef.current));
    }, PRICE_REFRESH_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [fetchPriceLine]);

  useEffect(() => {
    setPriceDataMap({});
    activeExchangesRef.current.forEach(exch => fetchPriceLine(exch, priceBucket));
  }, [fetchPriceLine, priceBucket]);

  useEffect(() => { fetchAvailableExchanges(); }, [fetchAvailableExchanges]);

  // ── Exchange management ───────────────────────────────────────────────────────

  const addExchange = useCallback((exch) => {
    const color = LINE_COLORS[colorIdxRef.current % LINE_COLORS.length];
    colorIdxRef.current += 1;
    setColorMap(prev => ({ ...prev, [exch]: color }));
    setActiveExchanges(prev => [...prev, exch]);
    fetchPriceLine(exch, priceBucketRef.current);
  }, [fetchPriceLine]);

  const removeExchange = useCallback((exch) => {
    setActiveExchanges(prev => prev.filter(e => e !== exch));
    setPriceDataMap(prev  => { const n = { ...prev };  delete n[exch]; return n; });
    setPriceErrorMap(prev => { const n = { ...prev };  delete n[exch]; return n; });
    setHiddenDatasets(prev => {
      const n = new Set(prev);
      n.delete(exch); n.delete(`${exch}__bid_price`); n.delete(`${exch}__ask_price`);
      return n;
    });
  }, []);

  const toggleDataset = useCallback((key) => {
    setHiddenDatasets(prev => {
      const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
    });
  }, []);

  // ── Derived data ──────────────────────────────────────────────────────────────

  const mergedPriceData = useMemo(() => {
    if (!activeExchanges.length) return [];
    const timeSet = new Set();
    activeExchanges.forEach(exch => (priceDataMap[exch] || []).forEach(d => timeSet.add(d.time_ms)));
    const lookups = {};
    activeExchanges.forEach(exch => {
      lookups[exch] = {};
      (priceDataMap[exch] || []).forEach(d => { lookups[exch][d.time_ms] = d; });
    });
    return [...timeSet].sort((a, b) => a - b).map(t => {
      const pt = { time_ms: t };
      activeExchanges.forEach(exch => {
        const d = lookups[exch][t];
        pt[exch] = d?.price ?? null;
        if (d?.bid_price != null) pt[`${exch}__bid_price`] = d.bid_price;
        if (d?.bid_qty   != null) pt[`${exch}__bid_qty`]   = d.bid_qty;
        if (d?.ask_price != null) pt[`${exch}__ask_price`] = d.ask_price;
        if (d?.ask_qty   != null) pt[`${exch}__ask_qty`]   = d.ask_qty;
      });
      return pt;
    });
  }, [priceDataMap, activeExchanges]);

  // Exchanges that have at least one data point with bid/ask price in the dataset
  const exchangesWithOrderBook = useMemo(() => {
    const set = new Set();
    activeExchanges.forEach(exch => {
      if ((priceDataMap[exch] || []).some(d => d.bid_price != null || d.ask_price != null))
        set.add(exch);
    });
    return set;
  }, [priceDataMap, activeExchanges]);

  const [priceZoom, setPriceZoom] = useState(null);
  const [refAreaLeft, setRefAreaLeft] = useState(null);
  const [refAreaRight, setRefAreaRight] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const resetPriceZoom = useCallback(() => setPriceZoom(null), []);
  const cancelSelection = useCallback(() => {
    setIsSelecting(false); setRefAreaLeft(null); setRefAreaRight(null);
  }, []);
  const onPriceMouseUp = useCallback(() => {
    if (refAreaLeft != null && refAreaRight != null && refAreaLeft !== refAreaRight) {
      const [l, r] = refAreaLeft <= refAreaRight
        ? [refAreaLeft, refAreaRight]
        : [refAreaRight, refAreaLeft];
      setPriceZoom([l, r]);
    }
    setIsSelecting(false); setRefAreaLeft(null); setRefAreaRight(null);
  }, [refAreaLeft, refAreaRight]);

  // Reset price zoom on bucket or exchange count changes
  useEffect(() => { resetPriceZoom(); }, [priceBucket, activeExchanges.length, resetPriceZoom]);

  // AMW ticks — adapt interval to zoom range for natural label density
  const amwTicks = useMemo(() => {
    if (amwData.length < 2) return [];
    const [minMs, maxMs] = amwZoom ?? [amwData[0].time_ms, amwData[amwData.length - 1].time_ms];
    const iv = amwZoom ? niceInterval(maxMs - minMs) : (AMW_TICK_INTERVAL_MS[amwWindow] || 5*60*1000);
    return buildTicksFromDomain(minMs, maxMs, iv);
  }, [amwData, amwZoom, amwWindow]);

  const visiblePriceData = useMemo(() => {
    if (!priceZoom) return mergedPriceData;
    const [l, r] = priceZoom;
    return mergedPriceData.filter(d => d.time_ms >= l && d.time_ms <= r);
  }, [mergedPriceData, priceZoom]);

  // Price ticks — adapt to zoom range
  const priceTicks = useMemo(() => {
    if (mergedPriceData.length < 2) return [];
    const [minMs, maxMs] = priceZoom ?? [mergedPriceData[0].time_ms, mergedPriceData[mergedPriceData.length - 1].time_ms];
    const iv = priceZoom ? niceInterval(maxMs - minMs) : (PRICE_TICK_INTERVAL_MS[priceBucket] || 10*60*1000);
    return buildTicksFromDomain(minMs, maxMs, iv);
  }, [mergedPriceData, priceZoom, priceBucket]);

  const latestQty = amwData.length > 0 ? amwData[amwData.length - 1].qty_per_sec : null;
  const amwOption = WINDOW_OPTIONS.find(o => o.value === amwWindow);

  // Label interval for price X axis — when zoomed, use a nice interval
  const priceLabelEveryMs = priceZoom
    ? niceInterval((priceZoom[1] - priceZoom[0]) / 6)  // ~6 labels when zoomed
    : (PRICE_LABEL_EVERY_MIN[priceBucket] || 30) * 60000;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0d1117', display: 'flex', flexDirection: 'column',
      fontFamily: "'Courier New', Courier, monospace", color: '#c9d1d9', zIndex: 9999,
    }}>

      {/* ── Global header ──────────────────────────────────────────────────── */}
      <div style={{ padding: '6px 16px', borderBottom: '1px solid #30363d', display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0, background: '#161b22' }}>
        <span style={{ fontWeight: 'bold', color: '#fff', fontSize: '14px' }}>
          {exchange.toUpperCase()} &middot; {symbol} &middot; {contractType}
        </span>
        {activeTab === 'trades' && <>
          <span style={{ color: BUY_COLOR,  fontSize: '12px' }}>&#9632; buy qty</span>
          <span style={{ color: SELL_COLOR, fontSize: '12px' }}>&#9632; sell qty</span>
          <span style={{ color: '#8b949e',  fontSize: '11px' }}>10s buckets &middot; 1h window</span>
          {tradeCount > 0 && <span style={{ color: '#8b949e', fontSize: '11px' }}>{tradeCount.toLocaleString()} trades</span>}
          {lastUpdate && <span style={{ color: '#8b949e', fontSize: '11px', marginLeft: 'auto' }}>{lastUpdate.toLocaleTimeString()}</span>}
        </>}
        {activeTab === 'price' && <>
          <span style={{ color: '#8b949e', fontSize: '11px' }}>VWAP &middot; last 24h &middot; {priceBucket} buckets</span>
          <span style={{ color: '#8b949e', fontSize: '11px', marginLeft: 'auto' }}>
            {activeExchanges.map(exch => {
              const last = (priceDataMap[exch] || []).at(-1);
              return last ? (
                <span key={exch} style={{ marginLeft: '12px' }}>
                  <span style={{ color: colorMap[exch] }}>&#9632;</span>
                  {' '}{exch}: {last.price.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                </span>
              ) : null;
            })}
          </span>
        </>}
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', borderBottom: '1px solid #30363d', background: '#161b22', flexShrink: 0, paddingLeft: '8px' }}>
        {['trades', 'price'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={activeTab === tab ? TAB_ACTIVE : TAB_BASE}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ══ TRADES TAB ═════════════════════════════════════════════════════════ */}
      <div style={{ display: activeTab === 'trades' ? 'flex' : 'none', flex: 1, minHeight: 0, flexDirection: 'column' }}>

        {/* Histogram (lightweight-charts — zoom works natively) */}
        <div style={{ flex: '0 0 58%', minHeight: 0, position: 'relative' }}>
          {error
            ? <div style={{ padding: '20px', color: '#f85149', fontSize: '13px' }}>Error: {error}</div>
            : <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
          }
        </div>

        {/* AMW chart */}
        <div style={{ flex: '0 0 42%', minHeight: 0, display: 'flex', flexDirection: 'column', borderTop: '1px solid #30363d' }}>
          <div style={{ padding: '5px 16px', borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, background: '#161b22' }}>
            <span style={{ color: '#8b949e', fontSize: '12px' }}>qty / sec</span>
            <span style={{ color: '#58a6ff', fontSize: '12px' }}>&#9632; avg moving window</span>
            <select value={amwWindow} onChange={e => setAmwWindow(e.target.value)} style={selectStyle}>
              {WINDOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {amwOption && <span style={{ color: '#8b949e', fontSize: '11px' }}>{amwOption.rangeLabel}</span>}
            <span style={{ color: '#8b949e', fontSize: '11px', marginLeft: 'auto' }}>
              {amwError
                ? <span style={{ color: '#f85149' }}>{amwError}</span>
                : latestQty !== null ? `latest: ${latestQty.toFixed(6)}` : 'loading\u2026'}
            </span>
          </div>

          {/* AMW chart container — wheel listener attached here */}
          <div ref={amwChartRef} onDoubleClick={resetAmwZoom}
            style={{ flex: 1, minHeight: 0, paddingTop: '4px', position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={amwData} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                <XAxis
                  dataKey="time_ms" type="number" scale="time"
                  domain={amwZoom ?? ['dataMin', 'dataMax']}
                  ticks={amwTicks}
                  tickFormatter={ms => formatTime(ms, amwWindow)}
                  tick={{ fill: '#8b949e', fontSize: 9 }} height={28}
                />
                <YAxis tick={{ fill: '#8b949e', fontSize: 9 }} width={60} />
                <Tooltip content={(p) => <AmwTooltip {...p} win={amwWindow} />} />
                <Line type="monotone" dataKey="qty_per_sec" name="qty/sec"
                  stroke="#58a6ff" dot={false} isAnimationActive={false} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
            <ZoomHint zoomed={!!amwZoom} onReset={resetAmwZoom} />
          </div>
        </div>
      </div>

      {/* ══ PRICE TAB ══════════════════════════════════════════════════════════ */}
      <div style={{ display: activeTab === 'price' ? 'flex' : 'none', flex: 1, minHeight: 0, flexDirection: 'column' }}>

        {/* Price sub-header — bucket selector only */}
        <div style={{ padding: '5px 16px', borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, background: '#161b22' }}>
          <span style={{ color: '#8b949e', fontSize: '12px' }}>bucket</span>
          <select value={priceBucket} onChange={e => setPriceBucket(e.target.value)} style={selectStyle}>
            {PRICE_BUCKET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Price chart container — drag to select zoom area, double-click to reset */}
        <div onDoubleClick={resetPriceZoom}
          style={{ flex: 1, minHeight: 0, paddingTop: '4px', position: 'relative' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={visiblePriceData} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}
              onMouseDown={(e) => { if (e?.activeLabel != null) { setRefAreaLeft(e.activeLabel); setIsSelecting(true); } }}
              onMouseMove={(e) => { if (isSelecting && e?.activeLabel != null) setRefAreaRight(e.activeLabel); }}
              onMouseUp={onPriceMouseUp}
              onMouseLeave={cancelSelection}
              style={{ cursor: isSelecting ? 'col-resize' : 'crosshair', userSelect: 'none' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis
                dataKey="time_ms" type="number" scale="time"
                domain={priceZoom ?? ['dataMin', 'dataMax']}
                ticks={priceTicks}
                tickFormatter={ms => {
                  const d = new Date(ms);
                  return d.getTime() % priceLabelEveryMs < (priceTicks[1] - priceTicks[0] || priceLabelEveryMs)
                    ? formatHHMM(ms) : '';
                }}
                tick={{ fill: '#8b949e', fontSize: 9 }} height={28}
              />
              <YAxis tick={{ fill: '#8b949e', fontSize: 9 }} width={80}
                tickFormatter={v => v.toLocaleString(undefined, { maximumSignificantDigits: 6 })}
                domain={['auto', 'auto']} />
              <Tooltip content={<PriceTooltip />} />
              {activeExchanges.filter(exch => !hiddenDatasets.has(exch)).map(exch => (
                <Line key={exch} type="monotone" dataKey={exch} name={exch}
                  stroke={colorMap[exch] || '#58a6ff'} dot={false}
                  isAnimationActive={false} connectNulls={false} />
              ))}
              {activeExchanges
                .filter(exch => exchangesWithOrderBook.has(exch))
                .flatMap(exch => [
                  !hiddenDatasets.has(`${exch}__bid_price`) &&
                    <Line key={`${exch}__bid_price`} type="monotone" dataKey={`${exch}__bid_price`} name={`${exch} bid`}
                      stroke={colorMap[exch] || '#58a6ff'} dot={false}
                      isAnimationActive={false} connectNulls={false}
                      strokeDasharray="3 3" strokeOpacity={0.7} />,
                  !hiddenDatasets.has(`${exch}__ask_price`) &&
                    <Line key={`${exch}__ask_price`} type="monotone" dataKey={`${exch}__ask_price`} name={`${exch} ask`}
                      stroke={colorMap[exch] || '#58a6ff'} dot={false}
                      isAnimationActive={false} connectNulls={false}
                      strokeDasharray="8 3" strokeOpacity={0.7} />,
                ].filter(Boolean))}
              {isSelecting && refAreaLeft != null && refAreaRight != null && (
                <ReferenceArea x1={refAreaLeft} x2={refAreaRight} fill="#58a6ff" fillOpacity={0.1} strokeOpacity={0.3} />
              )}
            </LineChart>
          </ResponsiveContainer>

          {/* ── Floating mini-menu ──────────────────────────────────────────── */}
          <div style={{
            position: 'absolute', top: 8, right: 8, zIndex: 10,
            background: 'rgba(22, 27, 34, 0.93)', border: '1px solid #30363d',
            borderRadius: '6px', padding: '8px 10px',
            fontSize: '11px', fontFamily: "'Courier New', Courier, monospace",
            minWidth: '148px', maxHeight: 'calc(100% - 24px)', overflowY: 'auto',
          }}>
            {availableExchanges.length === 0 && activeExchanges.length <= 1 && (
              <div style={{ color: '#444c56', fontSize: '10px', marginBottom: '6px' }}>Scanning Redis…</div>
            )}
            {[...activeExchanges, ...availableExchanges.filter(e => !activeExchanges.includes(e))].map(exch => {
              const isActive = activeExchanges.includes(exch);
              const color    = isActive ? (colorMap[exch] || '#58a6ff') : '#8b949e';
              const hasOB    = exchangesWithOrderBook.has(exch);
              const datasets = isActive ? [
                { key: exch,                  label: 'price', dash: null,  opacity: 1    },
                hasOB && { key: `${exch}__bid_price`, label: 'bid',   dash: '3 3', opacity: 0.7 },
                hasOB && { key: `${exch}__ask_price`, label: 'ask',   dash: '8 3', opacity: 0.7 },
              ].filter(Boolean) : [];
              return (
                <div key={exch} style={{ marginBottom: '6px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', userSelect: 'none', marginBottom: isActive ? '3px' : 0 }}>
                    <input type="checkbox" checked={isActive}
                      onChange={() => isActive ? removeExchange(exch) : addExchange(exch)}
                      style={{ margin: 0, accentColor: color, cursor: 'pointer', width: '11px', height: '11px' }} />
                    <span style={{ color, fontWeight: 'bold', flex: 1, letterSpacing: '0.02em' }}>{exch}</span>
                    {!!priceErrorMap[exch] && <span title={priceErrorMap[exch]} style={{ color: '#f85149', fontSize: '9px' }}>!</span>}
                  </label>
                  {datasets.map(({ key, label, dash, opacity }) => {
                    const on = !hiddenDatasets.has(key);
                    return (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', paddingLeft: '16px', marginBottom: '2px', userSelect: 'none' }}>
                        <input type="checkbox" checked={on} onChange={() => toggleDataset(key)}
                          style={{ margin: 0, accentColor: colorMap[exch] || '#58a6ff', cursor: 'pointer', width: '11px', height: '11px' }} />
                        <LineIndicator dashArray={dash} color={colorMap[exch] || '#58a6ff'} opacity={on ? opacity : 0.25} />
                        <span style={{ color: on ? '#c9d1d9' : '#444c56', fontSize: '10px' }}>{label}</span>
                      </label>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <ZoomHint zoomed={!!priceZoom} onReset={resetPriceZoom} />
        </div>
      </div>

    </div>
  );
};

export default ChartPage;
